import { makeAutoObservable } from 'mobx'
import { isEmail, isLink, PERSON_NAME_REGEX } from 'shared/lib'
import { getEmailDomain, getUrlDomain } from 'shared/lib/getDomains'
import { type INormalizedApiError } from 'shared/api'
import { type IRcsContactStepParams, type IResponseRcsCompliance } from 'entities/Rcs'
import { type RcsWizardStore } from 'pages/settings/pages/rcsV2/store/rcsWizardStore'

const EMAIL_DOMAIN_MISMATCH_WARNING =
  "It looks like your business email domain doesn't match your business website domain"
const NAME_MAX_LENGTH = 60
const BUSINESS_TITLE_MIN_LENGTH = 2
const BUSINESS_TITLE_MAX_LENGTH = 80
const EMAIL_MAX_LENGTH = 254
const WEBSITE_URL_MAX_LENGTH = 2048

export class ContactInfoStore {
  private _firstName = ''
  private _firstNameError = ''
  private _lastName = ''
  private _lastNameError = ''
  private _businessTitle = ''
  private _businessTitleError = ''
  private _businessEmail = ''
  private _businessEmailError = ''
  private _websiteUrl = ''
  private _websiteUrlError = ''
  private _consent = false

  constructor(private _wizardStore: RcsWizardStore) {
    makeAutoObservable(this)
  }

  get firstName() {
    return this._firstName
  }

  get firstNameError() {
    return this._firstNameError
  }

  get lastName() {
    return this._lastName
  }

  get lastNameError() {
    return this._lastNameError
  }

  get businessTitle() {
    return this._businessTitle
  }

  get businessTitleError() {
    return this._businessTitleError
  }

  get nameMaxLength() {
    return NAME_MAX_LENGTH
  }

  get businessTitleMaxLength() {
    return BUSINESS_TITLE_MAX_LENGTH
  }

  get emailMaxLength() {
    return EMAIL_MAX_LENGTH
  }

  get websiteUrlMaxLength() {
    return WEBSITE_URL_MAX_LENGTH
  }

  get businessEmail() {
    return this._businessEmail
  }

  get businessEmailError() {
    return this._businessEmailError
  }

  get websiteUrl() {
    return this._websiteUrl
  }

  get websiteUrlError() {
    return this._websiteUrlError
  }

  get consent() {
    return this._consent
  }

  private get _isFirstNameValid() {
    return PERSON_NAME_REGEX.test(this._firstName.trim())
  }

  private get _isLastNameValid() {
    return PERSON_NAME_REGEX.test(this._lastName.trim())
  }

  private get _isBusinessTitleValid() {
    return this._businessTitle.trim().length >= BUSINESS_TITLE_MIN_LENGTH
  }

  private get _isBusinessEmailValid() {
    return isEmail(this._businessEmail)
  }

  get isWebsiteUrlValid() {
    return isLink(this._websiteUrl)
  }

  private get _websiteUrlDomain() {
    try {
      return getUrlDomain(new URL(this._websiteUrl))
    } catch {
      return undefined
    }
  }

  get businessEmailDomainWarning() {
    if (!this._isBusinessEmailValid || !this.isWebsiteUrlValid) return ''

    const emailDomain = getEmailDomain(this._businessEmail)
    const urlDomain = this._websiteUrlDomain
    if (!emailDomain || !urlDomain) return ''

    return emailDomain === urlDomain ? '' : EMAIL_DOMAIN_MISMATCH_WARNING
  }

  get isUsFlow() {
    return this._wizardStore.isUsFlow
  }

  get params(): IRcsContactStepParams {
    return {
      first_name: this._firstName,
      last_name: this._lastName,
      business_title: this._businessTitle,
      business_email: this._businessEmail,
      business_website_URL: this.isUsFlow ? undefined : this._websiteUrl || undefined,
    }
  }

  get isNextDisabled() {
    return !(
      this._isFirstNameValid &&
      this._isLastNameValid &&
      this._isBusinessTitleValid &&
      this._isBusinessEmailValid &&
      (this.isUsFlow || this.isWebsiteUrlValid) &&
      this._consent
    )
  }

  setFirstName = (value: string) => {
    this._firstName = value
    if (this._firstNameError) this._firstNameError = ''
  }

  setLastName = (value: string) => {
    this._lastName = value
    if (this._lastNameError) this._lastNameError = ''
  }

  setBusinessTitle = (value: string) => {
    this._businessTitle = value
    if (this._businessTitleError) this._businessTitleError = ''
  }

  setBusinessEmail = (value: string) => {
    this._businessEmail = value
    this._businessEmailError = ''
  }

  setWebsiteUrl = (value: string) => {
    this._websiteUrl = value
    this._websiteUrlError = ''
  }

  toggleConsent = () => {
    this._consent = !this._consent
  }

  private _getEmailError = (value: string) => {
    if (!value) return 'Enter an email address'
    return isEmail(value) ? '' : 'Enter a valid email address'
  }

  private _getUrlError = (url: string) => {
    if (!url) return 'Enter a URL'
    return isLink(url) ? '' : 'Enter a valid URL'
  }

  validateFirstName = () => {
    if (!this._firstName.trim()) {
      this._firstNameError = 'Enter a first name'
      return
    }
    this._firstNameError = this._isFirstNameValid ? '' : 'Enter a valid first name'
  }

  validateLastName = () => {
    if (!this._lastName.trim()) {
      this._lastNameError = 'Enter a last name'
      return
    }
    this._lastNameError = this._isLastNameValid ? '' : 'Enter a valid last name'
  }

  validateBusinessTitle = () => {
    if (!this._businessTitle.trim()) {
      this._businessTitleError = 'Enter a business title'
      return
    }
    this._businessTitleError = this._isBusinessTitleValid
      ? ''
      : 'Business title must be at least 2 characters'
  }

  validateBusinessEmail = () => {
    this._businessEmailError = this._getEmailError(this._businessEmail)
  }

  validateWebsiteUrl = () => {
    this._websiteUrlError = this._getUrlError(this._websiteUrl)
  }

  initErrors = (errors: INormalizedApiError) => {
    this._firstNameError = errors.getError('first_name')
    this._lastNameError = errors.getError('last_name')
    this._businessTitleError = errors.getError('business_title')
    this._businessEmailError = errors.getError('business_email')
    this._websiteUrlError = errors.getError('business_website_URL')
  }

  init = (data: IResponseRcsCompliance) => {
    const contact = data.data?.contact_info
    if (!contact) return

    this._firstName = contact.first_name ?? ''
    this._lastName = contact.last_name ?? ''
    this._businessTitle = contact.business_title ?? ''
    this._businessEmail = contact.business_email ?? ''
    this._websiteUrl = contact.business_website_URL ?? ''
  }

  reset = () => {
    this._firstName = ''
    this._firstNameError = ''
    this._lastName = ''
    this._lastNameError = ''
    this._businessTitle = ''
    this._businessTitleError = ''
    this._businessEmail = ''
    this._businessEmailError = ''
    this._websiteUrl = ''
    this._websiteUrlError = ''
    this._consent = false
  }
}
